import type { ShopInfo } from './api';
import type { Strings } from './i18n';

/** One place for plan names, prices and features: the Plans page and every upgrade pop-up read from here. */
export type PlanCode = 'starter' | 'pro' | 'plus';
export type PaidPlan = Exclude<PlanCode, 'starter'>;
export type Plan = {
  code: PlanCode;
  name: string;
  monthly: number;
  /** Total per year, or null when the plan is monthly only. */
  yearly: number | null;
  trialDays: number;
  tagline: keyof Strings;
  features: (keyof Strings)[];
};

// Launch prices (decided 2026-10-08): Pro monthly only; Plus yearly is 25% off. Shopify App Pricing holds the real
// prices: keep these, the API catalog seed and the website in step with it.
export const PLANS: Plan[] = [
  { code: 'starter', name: 'Starter', monthly: 0, yearly: 0, trialDays: 0, tagline: 'starterTagline', features: ['fUnlimited', 'fLayouts', 'fHidePin', 'fTags6', 'fNoWatermark'] },
  { code: 'pro', name: 'Pro', monthly: 8, yearly: null, trialDays: 7, tagline: 'proTagline', features: ['fTagsAll', 'fComments', 'fAutoplay', 'fFloating', 'fMultiFeeds', 'phoneLayout'] },
  { code: 'plus', name: 'Plus', monthly: 19, yearly: 170, trialDays: 7, tagline: 'plusTagline', features: ['fPageFeeds', 'fUploads', 'fAnalytics'] },
];

export const planByCode = (code: PlanCode): Plan => PLANS.find(p => p.code === code) ?? PLANS[0];

/** Every paid feature the admin can lock, the plan that unlocks it, and the name the upgrade pop-up shows. */
export type PlanFeature =
  | 'floating'
  | 'video_autoplay'
  | 'mobile_layout'
  | 'page_filter'
  | 'multiple_feeds'
  | 'tags'
  | 'file_uploads'
  | 'analytics'
  | 'customer_posts'
  | 'comments';

export const FEATURES: Record<PlanFeature, { plan: PaidPlan; title: keyof Strings }> = {
  floating: { plan: 'pro', title: 'layoutFloating' },
  video_autoplay: { plan: 'pro', title: 'fAutoplay' },
  mobile_layout: { plan: 'pro', title: 'phoneLayout' },
  multiple_feeds: { plan: 'pro', title: 'fMultiFeeds' },
  tags: { plan: 'pro', title: 'fTagsAll' },
  page_filter: { plan: 'plus', title: 'fPageFeeds' },
  file_uploads: { plan: 'plus', title: 'fUploads' },
  analytics: { plan: 'plus', title: 'fAnalytics' },
  customer_posts: { plan: 'plus', title: 'customerPosts' },
  comments: { plan: 'pro', title: 'fComments' },
};

type Entitlements = ShopInfo['shop']['entitlements'];

/** Whether the shop's plan includes the feature (`tags` = tags on every post, i.e. no tagged-post limit). */
export function hasFeature(entitlements: Entitlements | undefined, feature: PlanFeature): boolean {
  switch (feature) {
    case 'floating':
      return Boolean(entitlements?.layouts?.includes('floating'));
    case 'multiple_feeds': {
      const max = entitlements?.max_feeds;
      return max === null || (typeof max === 'number' && max > 1);
    }
    case 'tags':
      return entitlements?.max_tagged_posts === null;
    case 'customer_posts':
      return Boolean(entitlements?.ugc_moderation);
    case 'comments':
      return Boolean(entitlements?.comment_inbox);
    default:
      return Boolean(entitlements?.[feature]);
  }
}
